(() => {
  'use strict';

  const SELECTOR = '[data-alnashir-carousel]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionCoordinator = window.alnashirCarouselMotion || { active: null };
  window.alnashirCarouselMotion = motionCoordinator;

  function initCarousel(root) {
    if (root.dataset.alnashirCarouselReady === 'true') return;

    const viewport = root.querySelector('.aln-carousel__viewport');
    const track = root.querySelector('[data-carousel-track]');
    const allItems = Array.from(root.querySelectorAll('[data-carousel-item]'));
    const itemCount = root.querySelectorAll('[data-carousel-item][data-copy="1"]').length;
    if (!viewport || !track || itemCount < 2 || allItems.length !== itemCount * 3) return;

    root.dataset.alnashirCarouselReady = 'true';

    const moveMs = Math.max(300, Number(root.dataset.moveMs) || 1800);
    const pauseMs = Math.max(1000, Number(root.dataset.pauseMs) || 3000);
    const interactionPauseMs = Math.max(5000, Number(root.dataset.interactionPauseMs) || 5000);
    const startDelayMs = Math.max(500, Number(root.dataset.startDelayMs) || 3000);
    const pauseOnHover = root.dataset.pauseOnHover !== 'false';
    const initializedAt = performance.now();
    const blockers = new Set();
    const cleanups = [];
    let currentIndex = itemCount;
    let visibleCount = 1;
    let stepWidth = 1;
    let autoplayTimer = 0;
    let settleTimer = 0;
    let resizeTimer = 0;
    let clickBlockTimer = 0;
    let momentumFrame = 0;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartPosition = 0;
    let pointerLastX = 0;
    let pointerLastTime = 0;
    let pointerVelocity = 0;
    let dragged = false;
    let blockNextClick = false;
    let lastObservedWidth = viewport.clientWidth;
    let hasAutoAdvanced = false;
    let interactionHoldUntil = 0;
    let ignoreHoverUntilLeave = false;

    function addEvent(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    function getVisibleCount() {
      const width = viewport.clientWidth;
      const minimumWidth = width < 600 ? 105 : 145;
      return Math.min(itemCount, Math.max(2, Math.min(10, Math.floor(width / minimumWidth))));
    }

    function getRenderedPosition() {
      const transform = window.getComputedStyle(track).transform;
      if (!transform || transform === 'none') return -currentIndex * stepWidth;
      try {
        return new DOMMatrixReadOnly(transform).m41;
      } catch (_error) {
        const values = transform.match(/matrix(?:3d)?\(([^)]+)\)/);
        if (!values) return -currentIndex * stepWidth;
        const parts = values[1].split(',').map(Number);
        return parts.length === 16 ? parts[12] : parts[4];
      }
    }

    function setPosition(position, animate) {
      animate = animate === true;
      track.style.transition = animate
        ? `transform ${moveMs}ms cubic-bezier(0.22, 1, 0.36, 1)`
        : 'none';
      track.style.transform = `translate3d(${position}px, 0, 0)`;
    }

    function wrapPosition(position, adjustPointerStart) {
      const copyWidth = itemCount * stepWidth;
      let index = -position / stepWidth;
      // Keep the rendered start inside the middle copy. Re-centering by one
      // complete copy is visually identical and leaves a full copy available
      // on either side throughout a fast drag or momentum coast.
      while (index < itemCount) {
        position -= copyWidth;
        index += itemCount;
        if (adjustPointerStart) pointerStartPosition -= copyWidth;
      }
      while (index >= itemCount * 2) {
        position += copyWidth;
        index -= itemCount;
        if (adjustPointerStart) pointerStartPosition += copyWidth;
      }
      return { position, index };
    }

    function cancelMomentum() {
      if (momentumFrame) window.cancelAnimationFrame(momentumFrame);
      momentumFrame = 0;
      root.classList.remove('is-coasting', 'is-settling');
      delete root.dataset.wheelMotion;
    }

    function updateInterface() {
      allItems.forEach((item) => {
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      });
      const visibleStart = Math.round(currentIndex);
      for (let offset = 0; offset < visibleCount; offset += 1) {
        const item = allItems[visibleStart + offset];
        if (!item) continue;
        item.removeAttribute('aria-hidden');
        item.setAttribute('tabindex', '0');
      }
    }

    function normalizeIndex() {
      const rounded = Math.round(currentIndex);
      const logicalIndex = ((rounded % itemCount) + itemCount) % itemCount;
      currentIndex = itemCount + logicalIndex;
      setPosition(-currentIndex * stepWidth, false);
      updateInterface();
    }

    function clearTimers() {
      window.clearTimeout(autoplayTimer);
      window.clearTimeout(settleTimer);
      autoplayTimer = 0;
      settleTimer = 0;
    }

    function canAutoplay() {
      return blockers.size === 0 && !document.hidden && !reducedMotion.matches && itemCount > visibleCount;
    }

    function schedule(delay) {
      if (typeof delay !== 'number') delay = pauseMs;
      window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;
      if (!canAutoplay()) return;
      autoplayTimer = window.setTimeout(autoAdvance, delay);
    }

    function finishMovement(continueAutoplay) {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        normalizeIndex();
        if (motionCoordinator.active === root) motionCoordinator.active = null;
        if (continueAutoplay) schedule(pauseMs);
      }, moveMs + 34);
    }

    function moveTo(index, continueAutoplay) {
      continueAutoplay = continueAutoplay === true;
      currentIndex = index;
      setPosition(-currentIndex * stepWidth, true);
      updateInterface();
      finishMovement(continueAutoplay);
    }

    function autoAdvance() {
      if (!canAutoplay()) return;
      if (motionCoordinator.active && motionCoordinator.active !== root) {
        schedule(200);
        return;
      }
      motionCoordinator.active = root;
      hasAutoAdvanced = true;
      const relativeIndex = currentIndex - itemCount;
      const currentPage = Math.round(relativeIndex / visibleCount);
      moveTo(itemCount + ((currentPage + 1) * visibleCount), true);
    }

    function freeze(reason) {
      blockers.add(reason);
      const renderedPosition = getRenderedPosition();
      cancelMomentum();
      clearTimers();
      setPosition(renderedPosition, false);
      currentIndex = -renderedPosition / stepWidth;
      if (motionCoordinator.active === root) motionCoordinator.active = null;
    }

    function resume(reason) {
      blockers.delete(reason);
      let delay = reason === 'resize' && !hasAutoAdvanced
        ? Math.max(0, startDelayMs - (performance.now() - initializedAt))
        : pauseMs;
      delay = Math.max(delay, interactionHoldUntil - performance.now());
      schedule(delay);
    }

    function finishUserMotion() {
      root.classList.remove('is-coasting');
      root.classList.add('is-settling');
      root.dataset.wheelMotion = 'settling';
      currentIndex = Math.round(currentIndex);
      track.style.transition = 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)';
      track.style.transform = `translate3d(${-currentIndex * stepWidth}px, 0, 0)`;
      updateInterface();
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        normalizeIndex();
        root.classList.remove('is-settling');
        delete root.dataset.wheelMotion;
        interactionHoldUntil = performance.now() + interactionPauseMs;
        resume('drag');
      }, 450);
    }

    function startMomentum(initialVelocity) {
      cancelMomentum();
      if (reducedMotion.matches || Math.abs(initialVelocity) < 0.04) {
        finishUserMotion();
        return;
      }

      let position = getRenderedPosition();
      let velocity = Math.max(-2.4, Math.min(2.4, initialVelocity));
      let previousTime = performance.now();
      const startedAt = previousTime;
      root.classList.add('is-coasting');
      root.dataset.wheelMotion = 'coasting';

      const coast = (now) => {
        const elapsed = now - startedAt;
        const frameTime = Math.min(32, Math.max(1, now - previousTime));
        previousTime = now;
        position += velocity * frameTime;
        const wrapped = wrapPosition(position, false);
        position = wrapped.position;
        currentIndex = wrapped.index;
        setPosition(position, false);
        velocity *= Math.pow(0.93, frameTime / 16.67);

        if (Math.abs(velocity) <= 0.025 || elapsed >= 1800) {
          momentumFrame = 0;
          finishUserMotion();
          return;
        }
        momentumFrame = window.requestAnimationFrame(coast);
      };

      momentumFrame = window.requestAnimationFrame(coast);
    }

    function measure() {
      const renderedPosition = getRenderedPosition();
      const previousStep = stepWidth;
      const logicalPosition = previousStep > 1 ? (-renderedPosition / previousStep) : currentIndex;
      visibleCount = getVisibleCount();
      root.dataset.visibleItems = String(visibleCount);

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const itemWidth = Math.max(1, (viewport.clientWidth - (gap * (visibleCount - 1))) / visibleCount);
      root.style.setProperty('--aln-item-width', `${itemWidth}px`);

      stepWidth = itemWidth + gap;
      currentIndex = Number.isFinite(logicalPosition) ? logicalPosition : itemCount;
      normalizeIndex();
    }

    function moveManually(direction, event) {
      const renderedPosition = getRenderedPosition();
      clearTimers();
      currentIndex = -renderedPosition / stepWidth;
      const relativeIndex = currentIndex - itemCount;
      const currentPage = Math.round(relativeIndex / visibleCount);
      interactionHoldUntil = performance.now() + interactionPauseMs;
      moveTo(itemCount + ((currentPage + direction) * visibleCount), false);
      if (event && event.detail > 0 && event.currentTarget) event.currentTarget.blur();
      schedule(interactionPauseMs + moveMs);
    }

    function onViewportKeydown(event) {
      if (event.target !== viewport) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      moveManually(root.dir === 'rtl' ? -direction : direction, event);
    }

    function onPointerDown(event) {
      if (!event.isPrimary || event.button > 0) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      freeze('drag');
      pointerStartPosition = getRenderedPosition();
      pointerLastX = event.clientX;
      pointerLastTime = performance.now();
      pointerVelocity = 0;
      dragged = false;
    }

    function onPointerMove(event) {
      if (event.pointerId !== pointerId) return;
      const delta = event.clientX - pointerStartX;
      const now = performance.now();
      const elapsed = Math.max(1, now - pointerLastTime);
      const instantVelocity = (event.clientX - pointerLastX) / elapsed;
      pointerVelocity = (pointerVelocity * 0.35) + (instantVelocity * 0.65);
      pointerLastX = event.clientX;
      pointerLastTime = now;
      const dragThreshold = event.pointerType === 'touch' ? 10 : 6;
      if (!dragged && Math.abs(delta) > dragThreshold) {
        dragged = true;
        root.classList.add('is-dragging');
        try {
          if (viewport.setPointerCapture) viewport.setPointerCapture(pointerId);
        } catch (_error) {
          // Some synthetic pointers cannot be captured; dragging still works.
        }
      }
      if (!dragged) return;
      event.preventDefault();
      const wrapped = wrapPosition(pointerStartPosition + delta, true);
      setPosition(wrapped.position, false);
      currentIndex = wrapped.index;
    }

    function onPointerEnd(event) {
      if (event.pointerId !== pointerId) return;
      let useMomentum = false;
      if (dragged) {
        blockNextClick = true;
        window.clearTimeout(clickBlockTimer);
        clickBlockTimer = window.setTimeout(() => {
          blockNextClick = false;
        }, 500);
        if (event.type === 'pointerup') {
          const wrapped = wrapPosition(pointerStartPosition + (event.clientX - pointerStartX), false);
          setPosition(wrapped.position, false);
          currentIndex = wrapped.index;
          useMomentum = Math.abs(pointerVelocity) >= 0.04;
        }
        ignoreHoverUntilLeave = event.pointerType === 'mouse' && root.matches(':hover');
        blockers.delete('hover');
        blockers.delete('focus');
      }
      root.classList.remove('is-dragging');
      try {
        if (viewport.hasPointerCapture && viewport.hasPointerCapture(pointerId)) {
          viewport.releasePointerCapture(pointerId);
        }
      } catch (_error) {
        // The browser may release capture before pointercancel is dispatched.
      }
      pointerId = null;
      if (dragged) {
        if (useMomentum) startMomentum(pointerVelocity);
        else finishUserMotion();
      } else {
        resume('drag');
      }
    }

    function onClick(event) {
      if (!blockNextClick) return;
      event.preventDefault();
      event.stopPropagation();
      window.clearTimeout(clickBlockTimer);
      blockNextClick = false;
    }

    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        freeze('resize');
        measure();
        resume('resize');
      }, 120);
    }

    function onVisibilityChange() {
      if (document.hidden) freeze('hidden');
      else resume('hidden');
    }

    function onReducedMotionChange() {
      if (reducedMotion.matches) freeze('motion');
      else resume('motion');
    }

    addEvent(viewport, 'pointerdown', onPointerDown);
    addEvent(viewport, 'pointermove', onPointerMove, { passive: false });
    addEvent(viewport, 'pointerup', onPointerEnd);
    addEvent(viewport, 'pointercancel', onPointerEnd);
    addEvent(viewport, 'click', onClick, true);
    addEvent(viewport, 'keydown', onViewportKeydown);
    if (pauseOnHover) {
      addEvent(root, 'pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !ignoreHoverUntilLeave) freeze('hover');
      });
      addEvent(root, 'pointerleave', (event) => {
        if (event.pointerType === 'mouse') {
          const wasIgnoringHover = ignoreHoverUntilLeave;
          ignoreHoverUntilLeave = false;
          if (!wasIgnoringHover) resume('hover');
        }
      });
    }
    addEvent(root, 'focusin', () => freeze('focus'));
    addEvent(root, 'focusout', (event) => {
      if (!root.contains(event.relatedTarget)) resume('focus');
    });
    addEvent(document, 'visibilitychange', onVisibilityChange);
    if (typeof reducedMotion.addEventListener === 'function') {
      addEvent(reducedMotion, 'change', onReducedMotionChange);
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(onReducedMotionChange);
      cleanups.push(() => reducedMotion.removeListener(onReducedMotionChange));
    }

    if (typeof window.ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver((entries) => {
        const firstEntry = entries[0];
        const nextWidth = (firstEntry && firstEntry.contentRect.width) || viewport.clientWidth;
        if (Math.abs(nextWidth - lastObservedWidth) < 1) return;
        lastObservedWidth = nextWidth;
        onResize();
      });
      resizeObserver.observe(viewport);
      cleanups.push(() => resizeObserver.disconnect());
    } else {
      addEvent(window, 'resize', onResize);
    }

    measure();
    schedule(startDelayMs);

    root.alnashirCarouselDestroy = () => {
      cancelMomentum();
      clearTimers();
      if (motionCoordinator.active === root) motionCoordinator.active = null;
      window.clearTimeout(resizeTimer);
      window.clearTimeout(clickBlockTimer);
      cleanups.forEach((cleanup) => cleanup());
      delete root.dataset.alnashirCarouselReady;
      delete root.alnashirCarouselDestroy;
    };
  }

  function initWithin(scope) {
    scope = scope || document;
    if (scope.matches && scope.matches(SELECTOR)) initCarousel(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(SELECTOR).forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin());
  } else {
    initWithin();
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target.querySelectorAll) return;
    event.target.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.alnashirCarouselDestroy) root.alnashirCarouselDestroy();
    });
  });
})();

